// eslint-disable-next-line no-unused-vars
import { fromEvent, Unsubscribable, Subject, interval } from 'rxjs';
import { DestroySubscribers, CombineSubscriptions } from 'ngx-destroy-subscribers';
import { debounceTime, tap } from 'rxjs/operators';
import { FormatHelper } from '../helpers/format-helper';
import FormController from './form_controller';

@DestroySubscribers({
  destroyFunc: 'disconnect'
})
export default class BookingDetailsController extends FormController {
  static readonly BOOKING_SIGNATURE_FIELDS = ['listing_id', 'item_id', 'booking[occupancy][adults]'];

  public static targets = ['bookingDetailForm', 'summary', 'countdownTimer', 'input', 'editGroupSize', 'staticGroupSize',
    'addressCountryCode', 'addressPostalCode', 'addressHouseNumber', 'addressLine1StreetInput', 'addressLine1AddressInput'
  ];

  private formSubject: Subject<any> = new Subject();
  private readonly bookingDetailFormTarget!: HTMLFormElement;
  private readonly inputTargets!: HTMLElement[];
  private readonly summaryTargets!: HTMLElement[];
  private readonly countdownTimerTarget!: HTMLElement;
  private readonly hasCountdownTimerTarget: boolean;
  private readonly editGroupSizeTarget!: HTMLDivElement;
  private readonly staticGroupSizeTarget!: HTMLDivElement;
  private readonly addressCountryCodeTarget!: HTMLSelectElement;
  private readonly addressPostalCodeTarget!: HTMLInputElement;
  private readonly addressHouseNumberTarget!: HTMLInputElement;
  private readonly addressLine1StreetInputTarget!: HTMLInputElement;
  private readonly addressLine1AddressInputTarget!: HTMLInputElement;

  @CombineSubscriptions()
  private subscriber: Unsubscribable;

  public connect() {
    super.connect();
    this.attachFormCalculation();
    if (this.hasCountdownTimerTarget) {
      this.setupCountdownTimer();
    }
    this.restoreFormData();
    this.updateSummary();
  }

  public onAjaxSuccessJson(event: CustomEvent) {
    super.onAjaxSuccessJson(event);
    const [data] = event.detail;
    if (data?.itemHTML) {
      this.summaryTargets.forEach(summary => {
        summary.innerHTML = data.itemHTML;
        summary.classList.remove('loading');
      })
    }
  }

  public onAjaxError(event: CustomEvent) {
    super.onAjaxError(event);
    this.summaryTargets.forEach(summary => {
      summary.classList.remove('loading');
    })
  }

  public amountChanged(event: CustomEvent) {
    const item = event.currentTarget as HTMLElement;
    const checkbox = item.parentNode.querySelector("input[type='checkbox']") as HTMLInputElement;
    checkbox.checked = true;
  }

  public editGroupSizeButton() {
    this.editGroupSizeTarget.classList.remove('d-none')
    this.staticGroupSizeTarget.classList.add('d-none')
  }

  public saveFormData() {
    const formData = new FormData(this.bookingDetailFormTarget);
    const formValues: Record<string, any> = {};

    formData.forEach((value, key) => {
      // Lookup element by name and value to ensure we're not saving the Rails hidden 'default value' checkbox
      // Special handling for line1 field
      if (key === 'booking[customer_address_attributes][line1]') {
        formValues[key] = value;
        return;
      }
      
      const element = this.findFormElementByNameAndValue(key, value);
      if (element && element.type !== 'hidden') {
        // Don't save amount if checkbox is unchecked
        if (key.includes('amount')) {
          const parentElement = element.parentElement;
          if (parentElement) {
            const checkbox = parentElement.querySelector('input[type="checkbox"]') as HTMLInputElement;
            if (checkbox && checkbox.checked) {
              formValues[key] = value;
              formValues[checkbox.name] = checkbox.checked;
            }
          }
        } else {
          if (formValues[key]) {
            if (!Array.isArray(formValues[key])) {
              formValues[key] = [formValues[key]];
            }
            formValues[key].push(value);
          } else {
            formValues[key] = value;
          }
        }
      } else if (BookingDetailsController.BOOKING_SIGNATURE_FIELDS.includes(key)) {
        formValues[key] = value;
      }
    });
    localStorage.setItem('bookingFormData', JSON.stringify(formValues));
  }

  private restoreFormData() {
    const savedData = localStorage.getItem('bookingFormData');
    if (!savedData) {
      return;
    }

    const formValues = JSON.parse(savedData);
    const currentData = Object.fromEntries(new FormData(this.bookingDetailFormTarget).entries());

    // Check if the saved formdata matches the current booking parameters
    if (BookingDetailsController.BOOKING_SIGNATURE_FIELDS.some((key) => formValues[key] !== currentData[key])) {
      return;
    }

    // First restore country code since it affects form layout
    const countryCode = formValues['booking[customer_address_attributes][country_code]'];
    if (countryCode) {
      this.addressCountryCodeTarget.value = countryCode;
    }

    Object.entries(formValues).forEach(([key, value]) => {
      const element = this.findFormElementByName(key);
      if (!element || element.type === 'hidden') return;

      if (element.type === 'radio') {
        const radio = this.bookingDetailFormTarget.querySelector(`input[name="${key}"][value="${value}"]`) as HTMLInputElement;
        if (radio) {
          radio.checked = true;
        }
      } else if (element.type === 'checkbox') {
        element.checked = ['1', 'true', true].includes(value as string | boolean);
      } else {
        switch (key) {
          case 'booking[customer_address_attributes][postal_code]':
            this.addressPostalCodeTarget.value = value as string;
            break;
          case 'booking[customer_address_attributes][house_number]':
            this.addressHouseNumberTarget.value = value as string;
            break;
          case 'booking[customer_address_attributes][line1]':
            const input = countryCode === 'NL' ? this.addressLine1StreetInputTarget : this.addressLine1AddressInputTarget;
            input.value = value as string;
            break;
          default:
            element.value = value as string;
        }
      }
    });
    this.updateSummary();
  }

  private findFormElementByName(name: string): HTMLInputElement {
    const elementByName = this.bookingDetailFormTarget.elements.namedItem(name);
    if (elementByName instanceof RadioNodeList) {
      // For each checkbox, Rails adds a hidden field with the same name and value '0' to ensure a value is always submitted;
      // Ignore the hidden field and select the first visible matching element
      return Array.from(elementByName).find((el: HTMLInputElement) => el.type !== 'hidden') as HTMLInputElement;
    } else {
      return elementByName as HTMLInputElement;
    }
  }
  private findFormElementByNameAndValue(name: string, value: any): HTMLInputElement | null {
    const element = this.findFormElementByName(name);
    if (element && element.value === value) {
      return element;
    }
    return null;
  }

  private attachFormCalculation() {
    this.inputTargets.forEach(input => {
      this.subscriber = fromEvent(input, 'change').subscribe(() => {
        this.formSubject.next();
      })
    });
    this.subscriber = this.formSubject.pipe(
      tap(() =>
        this.summaryTargets.forEach(summary => {
          summary.classList.add('loading')
        })
      ),
      debounceTime(100)
    ).subscribe(() => {
      this.updateSummary();
    })
  }

  private updateSummary() {
    const params = new URLSearchParams(new FormData(this.bookingDetailFormTarget) as any);
    const summaryTarget = this.summaryTargets[0];
    const baseUrl = summaryTarget.dataset.url;
    const url = new URL(baseUrl, window.location.origin);
    params.forEach((value, key) => url.searchParams.append(key, value));

    return fetch(url.toString(), {
      headers: { 'X-Error-Message-Only': 'true' },
      method: 'GET',
      redirect: 'error'
    })
      .then(res => res.ok ? res.text() : res.json().then(json => Promise.reject(json)))
      .then(text => {
        this.summaryTargets.forEach(summary => {
          summary.innerHTML = text;
          summary.classList.remove('loading');
        });
      })
      .catch(error => {
        const message = (error && error.errorMessage) ? error.errorMessage : summaryTarget.dataset.errorMessage;
        this.summaryTargets.forEach(summary => {
          summary.innerHTML = message;
          summary.classList.remove('loading');
        });
      });
  }

  private setupCountdownTimer() {
    this.subscriber = interval(1000).pipe(
    ).subscribe(() => {
      const now = new Date();
      const currentEndsAt = parseInt(this.countdownTimerTarget.dataset.bookingWindowEndsAt, 0);
      const delay = currentEndsAt - +(Math.floor(+now / 1000));
      this.countdownTimerTarget.innerHTML = `${FormatHelper.formatTime(delay)}`;
    });
  }
}
